/**
 * Maintenance (ROADMAP Phase 7 groundwork, migration 0057): every open ticket with its age and
 * whether it is overdue, the recurrences code has found (three of a kind in 180 days), and the
 * preventive work coming due. A person opens a ticket, sets its severity, progresses and
 * resolves it; the Ops & Maintenance agent's assessments arrive beside these later and never
 * overwrite them. Age, overdue, recurrence and due dates are the views' — the API says how in
 * `calc_method` — and this screen adds nothing up.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type OpsReviewJson,
  type PreventiveScheduleJson,
  type Property,
  type RecurrenceJson,
  type TicketAssessmentJson,
  type TicketJson,
  type TicketsResponse,
} from '../api.js';
import { EntityPicker, useEntityId } from '../components/EntityPicker.js';
import { formatMoney } from '../money.js';

const CATEGORIES = [
  'plumbing',
  'hvac',
  'electrical',
  'appliance',
  'structural',
  'pool_spa',
  'internet',
  'cleaning',
  'pest',
  'safety',
  'other',
] as const;
const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_vendor: 'Waiting on vendor',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
};

export function MaintenancePage(): ReactNode {
  const entityId = useEntityId();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const tickets = useQuery({
    queryKey: ['ops', 'tickets', entityId, showClosed],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<TicketsResponse>(`/api/ops/tickets?entity=${entityId}&open=${!showClosed}`),
  });
  const recurrences = useQuery({
    queryKey: ['ops', 'recurrences', entityId],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<{ recurrences: RecurrenceJson[] }>(`/api/ops/recurrences?entity=${entityId}`),
  });
  const schedules = useQuery({
    queryKey: ['ops', 'schedules'],
    queryFn: () =>
      api.get<{ schedules: PreventiveScheduleJson[]; calc_method: string | null }>(
        '/api/ops/schedules',
      ),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });
  const assessments = useQuery({
    queryKey: ['ops', 'assessments'],
    queryFn: () => api.get<{ assessments: TicketAssessmentJson[] }>('/api/ops/assessments'),
  });
  const review = useQuery({
    queryKey: ['ops', 'review'],
    queryFn: () => api.get<{ review: OpsReviewJson | null }>('/api/ops/review'),
  });
  const assessmentFor = new Map((assessments.data?.assessments ?? []).map((a) => [a.ticket_id, a]));

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['ops'] });
  };
  const create = useMutation({
    mutationFn: (body: unknown) => api.post<{ ticket: TicketJson }>('/api/ops/tickets', body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: unknown }) =>
      api.patch<{ ticket: TicketJson }>(`/api/ops/tickets/${input.id}`, input.body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const markDone = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/api/ops/schedules/${id}`, {
        lastDoneOn: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const onCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string): string => String(data.get(name) ?? '').trim();
    create.mutate({
      propertyId: text('property'),
      title: text('title'),
      description: text('description'),
      category: text('category'),
      severity: text('severity') === '' ? null : text('severity'),
    });
    form.reset();
  };

  const entityProperties = (properties.data?.properties ?? []).filter(
    (p) => p.entity_id === entityId,
  );
  const cents = (value: string | null): string => (value === null ? '—' : formatMoney(value));
  const age = (t: TicketJson): string => {
    if (t.age_hours === null) return '—';
    return t.age_hours < 48 ? `${t.age_hours} h` : `${Math.floor(t.age_hours / 24)} d`;
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Maintenance</h1>
          <p className="subtitle">
            Every open issue with how long it has waited, the failures that keep coming back, and
            the preventive work coming due. A person sets severity and resolves; the agent, when it
            is on, assesses beside these and never over them.
          </p>
        </div>
        <EntityPicker />
      </header>

      {error && <p className="error">{error}</p>}

      <section className="card">
        <h2>Open a ticket</h2>
        <form onSubmit={onCreate} className="filters">
          <select name="property" required defaultValue="">
            <option value="" disabled>
              Property
            </option>
            {entityProperties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <input name="title" placeholder="What is wrong" required maxLength={200} />
          <select name="category" defaultValue="other">
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replace('_', ' / ')}
              </option>
            ))}
          </select>
          <select name="severity" defaultValue="">
            <option value="">Severity: unrated</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input name="description" placeholder="Details (optional)" maxLength={4000} />
          <button type="submit" disabled={create.isPending || entityProperties.length === 0}>
            Open ticket
          </button>
        </form>
      </section>

      <section className="card">
        <h2>
          Tickets{' '}
          <label className="small muted" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />{' '}
            show resolved and cancelled
          </label>
        </h2>
        {tickets.data && tickets.data.tickets.length === 0 && (
          <p className="muted">No tickets. That is a good day.</p>
        )}
        {tickets.data && tickets.data.tickets.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Ticket</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Agent says</th>
                  <th>Status</th>
                  <th className="num">Age</th>
                  <th className="num">Est.</th>
                  <th className="num">Actual</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tickets.data.tickets.map((t) => (
                  <tr key={t.id}>
                    <td>{t.property_name}</td>
                    <td>
                      <strong>{t.title}</strong>
                      {t.description && <div className="small muted">{t.description}</div>}
                      {t.resolution && <div className="small muted">Resolved: {t.resolution}</div>}
                    </td>
                    <td>{t.category.replace('_', ' / ')}</td>
                    <td>
                      <select
                        value={t.severity ?? ''}
                        onChange={(e) =>
                          update.mutate({
                            id: t.id,
                            body: { severity: e.target.value === '' ? null : e.target.value },
                          })
                        }
                        disabled={t.status === 'resolved' || t.status === 'cancelled'}
                      >
                        <option value="">unrated</option>
                        {SEVERITIES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="small">
                      {(() => {
                        const a = assessmentFor.get(t.id);
                        if (!a) return <span className="muted">—</span>;
                        return (
                          <span title={a.why.join(' ')}>
                            <strong>{a.severity}</strong>
                            {a.escalate ? ' · escalate' : ''}
                            {a.guest_impact ? ' · guest affected' : ''}
                            <span className="muted"> ({a.confidence})</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <span
                        className={`chip ${t.is_overdue ? 'chip-attention' : t.status === 'resolved' ? 'chip-ok' : ''}`}
                      >
                        {t.is_overdue ? 'OVERDUE' : (STATUS_LABEL[t.status] ?? t.status)}
                      </span>
                    </td>
                    <td className="num">{age(t)}</td>
                    <td className="num">{cents(t.estimated_cost_cents)}</td>
                    <td className="num">{cents(t.actual_cost_cents)}</td>
                    <td>
                      {t.status !== 'resolved' && t.status !== 'cancelled' && (
                        <>
                          {t.status === 'open' && (
                            <button
                              type="button"
                              className="link"
                              onClick={() =>
                                update.mutate({ id: t.id, body: { status: 'in_progress' } })
                              }
                            >
                              start
                            </button>
                          )}{' '}
                          <button
                            type="button"
                            className="link"
                            onClick={() => {
                              const resolution = window.prompt('What was done?') ?? '';
                              if (resolution.trim() === '') return;
                              update.mutate({
                                id: t.id,
                                body: { status: 'resolved', resolution: resolution.trim() },
                              });
                            }}
                          >
                            resolve
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tickets.data?.calc_method && (
          <p className="muted small">Age and overdue: {tickets.data.calc_method}</p>
        )}
      </section>

      <section className="card">
        <h2>The agent&apos;s morning review</h2>
        {review.data && review.data.review === null && (
          <p className="muted">
            No review yet. The Ops &amp; Maintenance agent runs at 06:25 Eastern when its flag is
            on; the assessments above and the risks below are its.
          </p>
        )}
        {review.data?.review && (
          <>
            <p>{review.data.review.summary}</p>
            {review.data.review.readiness_risks.length > 0 && (
              <ul>
                {review.data.review.readiness_risks.map((r) => (
                  <li key={r.reservationId}>
                    <strong>Check-in at risk</strong> — {r.openTurnoverTasks} open turnover task
                    {r.openTurnoverTasks === 1 ? '' : 's'}, {r.blockingTickets} blocking ticket
                    {r.blockingTickets === 1 ? '' : 's'}: {r.recommendation}
                  </li>
                ))}
              </ul>
            )}
            {review.data.review.recurrences.length > 0 && (
              <ul>
                {review.data.review.recurrences.map((r) => (
                  <li key={`${r.propertyId}-${r.category}`}>
                    <strong>
                      {r.category.replace('_', ' / ')}, {r.ticketCount} times
                    </strong>{' '}
                    — {r.finding} {r.recommendation}
                  </li>
                ))}
              </ul>
            )}
            {review.data.review.preventive_overdue.length > 0 && (
              <ul>
                {review.data.review.preventive_overdue.map((p) => (
                  <li key={p.scheduleId}>
                    <strong>{p.daysOverdue} days late</strong> — {p.recommendation}
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">
              Confidence {review.data.review.confidence} · {review.data.review.model ?? 'model'} ·
              run {review.data.review.run_id.slice(0, 8)} ·{' '}
              {new Date(review.data.review.generated_at).toLocaleString()}
              {review.data.review.checked_and_quiet.length > 0
                ? ` · quiet: ${review.data.review.checked_and_quiet.join(', ')}`
                : ''}
            </p>
          </>
        )}
      </section>

      <section className="card">
        <h2>Keeps coming back</h2>
        {recurrences.data && recurrences.data.recurrences.length === 0 && (
          <p className="muted">
            No property has had three tickets of one kind in the last 180 days.
          </p>
        )}
        {recurrences.data && recurrences.data.recurrences.length > 0 && (
          <ul>
            {recurrences.data.recurrences.map((r) => (
              <li key={`${r.property_id}-${r.category}`}>
                <strong>{r.property_name}</strong> · {r.category.replace('_', ' / ')} ·{' '}
                {r.ticket_count} tickets since {r.first_reported_at.slice(0, 10)}
                <div className="small muted">{r.titles.join(' · ')}</div>
              </li>
            ))}
          </ul>
        )}
        {recurrences.data?.recurrences[0] && (
          <p className="muted small">{recurrences.data.recurrences[0].calc_method}</p>
        )}
      </section>

      <section className="card">
        <h2>Preventive work</h2>
        {schedules.data && schedules.data.schedules.filter((s) => s.is_active).length === 0 && (
          <p className="muted">No preventive schedules yet.</p>
        )}
        {schedules.data && schedules.data.schedules.some((s) => s.is_active) && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Work</th>
                  <th>Property</th>
                  <th className="num">Every</th>
                  <th>Last done</th>
                  <th>Next due</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {schedules.data.schedules
                  .filter((s) => s.is_active)
                  .map((s) => (
                    <tr key={s.id}>
                      <td>
                        {s.title} <span className="small muted">{s.category}</span>
                      </td>
                      <td>{s.property_name ?? 'Every property'}</td>
                      <td className="num">{s.interval_days} d</td>
                      <td>{s.last_done_on ?? 'never'}</td>
                      <td>
                        <span
                          className={`chip ${s.days_until_due !== null && s.days_until_due <= 0 ? 'chip-attention' : ''}`}
                        >
                          {s.next_due_on ?? '—'}
                          {s.days_until_due !== null && s.days_until_due < 0
                            ? ` (${-s.days_until_due} d overdue)`
                            : ''}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="link"
                          onClick={() => markDone.mutate(s.id)}
                        >
                          done today
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {schedules.data?.calc_method && <p className="muted small">{schedules.data.calc_method}</p>}
      </section>
    </div>
  );
}
